/**
 * Zentrale Minispiel-Definitionen (Single Source of Truth)
 *
 * Diese Datei ist die EINZIGE Quelle für Minispiel-Definitionen.
 * Neue Minispiele hier hinzufügen - sie erscheinen automatisch überall.
 *
 * Verwendung:
 * - Server: const { MINIGAMES } = require('../src/shared/entities/minigames');
 * - Client: import { MINIGAMES } from './shared/entities/minigames';
 */

const { MINIGAME_MODE, SYNC_TYPE } = require('../core/minigameConstants');
const rarityModule = require('./rarity');
const { RARITY } = rarityModule;

// =============================================================================
// MINIGAME CATEGORIES
// =============================================================================

const MINIGAME_CATEGORY = {
  REACTION: 'reaction',     // Reaktionsspiele (Geschwindigkeit)
  MEMORY: 'memory',         // Gedächtnisspiele
  SKILL: 'skill',           // Geschicklichkeitsspiele
  LUCK: 'luck',             // Glücksspiele
  STRATEGY: 'strategy',     // Strategiespiele
};

// =============================================================================
// MINIGAME DEFINITIONS
// =============================================================================

/**
 * Alle verfügbaren Minispiele
 *
 * Jedes Minispiel hat:
 * - id: Eindeutige ID
 * - name: Anzeigename
 * - description: Kurzbeschreibung
 * - detailedDescription: Ausführliche Erklärung
 * - icon: Emoji für UI
 * - category: MINIGAME_CATEGORY
 * - mode: MINIGAME_MODE (simultaneous/sequential)
 * - syncType: SYNC_TYPE (nur bei simultaneous)
 * - duration: Maximale Dauer in ms
 * - component: Name der React-Komponente
 * - rewards: Belohnungen nach Rang
 * - rarity: Wie selten erscheint das Minispiel
 * - enabled: Ist das Minispiel aktiv?
 */
const MINIGAMES = {
  // === PLACEHOLDER (für Tests) ===
  PLACEHOLDER: {
    id: 'placeholder',
    name: 'Platzhalter',
    description: 'Ein einfaches Test-Minispiel',
    detailedDescription: 'Drücke so schnell wie möglich den Button! Das schnellste Ergebnis gewinnt. Dieses Minispiel dient als Platzhalter für die Entwicklung.',
    tutorial: 'Drücke den Button so schnell wie möglich!',
    icon: '🎮',
    category: MINIGAME_CATEGORY.REACTION,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    duration: 15000, // 15 Sekunden
    component: 'PlaceholderMinigame',
    rarity: RARITY.COMMON,
    enabled: false,
    rewards: {
      // Belohnungen nach Platzierung (Index = Platz - 1)
      // Für 2-6 Spieler, überschüssige Einträge werden ignoriert
      points: [5, 3, 2, 1, 0, 0],
      coins: [3, 2, 1, 0, 0, 0],
    },
    messages: {
      announce: 'Minispiel: Platzhalter!',
      start: 'Drücke den Button!',
      end: 'Zeit abgelaufen!',
    },
  },

  // === REACTION TEST ===
  REACTION_TEST: {
    id: 'reactionTest',
    name: 'Reaktionstest',
    description: 'Reagiere so schnell wie möglich!',
    detailedDescription: 'Warte bis der Bildschirm grün wird, dann klicke so schnell wie möglich! Wer am schnellsten reagiert, gewinnt. Zu frühes Klicken (Fehlstart) gibt Strafpunkte.',
    tutorial: 'WARTE bis der Bildschirm GRÜN wird, dann tippe so schnell du kannst! Fehlstarts = +500ms Strafe',
    icon: '⚡',
    category: MINIGAME_CATEGORY.REACTION,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    duration: 10000, // 10 Sekunden max
    component: 'ReactionTestMinigame',
    rarity: RARITY.COMMON,
    enabled: false,
    rewards: {
      points: [6, 4, 2, 1, 0, 0],
      coins: [4, 2, 1, 0, 0, 0],
    },
    config: {
      minDelay: 1000,    // Min. Wartezeit bis grün
      maxDelay: 5000,    // Max. Wartezeit bis grün
      falsePenalty: 500, // Strafe in ms bei Fehlstart
    },
    messages: {
      announce: 'Minispiel: Reaktionstest!',
      start: 'Warte auf Grün...',
      trigger: 'JETZT!',
      falseStart: 'Fehlstart! +500ms Strafe',
      end: 'Ergebnisse werden ausgewertet...',
    },
  },

  // === BUTTON MASHER ===
  BUTTON_MASHER: {
    id: 'buttonMasher',
    name: 'Button-Masher',
    description: 'Klicke bei Grün, stoppe bei Rot!',
    detailedDescription: 'Hämmere auf den Button wenn er grün ist! Bei Rot gibt es Abzug. Wer die meisten Klicks schafft, gewinnt.',
    tutorial: 'Grün = +1 Klick, Rot = -1 Klick! Achte auf die Farbe!',
    icon: '👆',
    category: MINIGAME_CATEGORY.REACTION,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    duration: 10000, // 10 Sekunden
    component: 'ButtonMasherMinigame',
    rarity: RARITY.COMMON,
    enabled: true,
    rewards: {
      // Punkte nach Klicks: dynamisch berechnet im Handler
      points: 'dynamic',
      coins: [], // Keine Münzen - nur Punkte
    },
    config: {
      // Timing
      duration: 10000,           // Gesamtdauer in ms

      // Grün/Rot Verhältnis
      greenRatio: 0.8,           // 80% der Zeit grün

      // Phasendauern (ms)
      greenPhaseMin: 1500,       // Grüne Phasen: 1.5-3s (länger)
      greenPhaseMax: 3000,
      redPhaseMin: 500,          // Rote Phasen: 0.5-1.5s (kürzer)
      redPhaseMax: 1500,
    },
    messages: {
      announce: 'Minispiel: Button-Masher!',
      start: 'Grün = Klicken, Rot = Stoppen!',
      end: 'Stop! Klicks werden gezählt...',
    },
  },

  // === MEMORY MATCH (für später) ===
  MEMORY_MATCH: {
    id: 'memoryMatch',
    name: 'Memory',
    description: 'Finde die Paare!',
    detailedDescription: 'Klassisches Memory-Spiel mit Karten. Finde alle Paare so schnell wie möglich! Weniger Züge = mehr Punkte.',
    tutorial: 'Decke Karten auf und finde alle Paare! Weniger Züge = mehr Punkte!',
    icon: '🃏',
    category: MINIGAME_CATEGORY.MEMORY,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    duration: 60000, // 60 Sekunden max
    component: 'MemoryMatchMinigame',
    rarity: RARITY.UNCOMMON,
    enabled: false, // Noch nicht implementiert
    rewards: {
      points: [8, 5, 3, 2, 1, 0],
      coins: [5, 3, 2, 1, 0, 0],
    },
    config: {
      gridSize: 4, // 4x4 Grid = 8 Paare
    },
    messages: {
      announce: 'Minispiel: Memory!',
      start: 'Finde alle Paare!',
      end: 'Fertig! Punkte werden berechnet...',
    },
  },

  // === LUCKY WHEEL (für später) ===
  LUCKY_WHEEL: {
    id: 'luckyWheel',
    name: 'Glücksrad',
    description: 'Drehe am Rad!',
    detailedDescription: 'Jeder Spieler dreht einmal am Glücksrad. Die Segmente haben unterschiedliche Werte - mit etwas Glück triffst du den Jackpot!',
    tutorial: 'Tippe um das Rad zu drehen! Die Segmente haben unterschiedliche Werte.',
    icon: '🎡',
    category: MINIGAME_CATEGORY.LUCK,
    mode: MINIGAME_MODE.SEQUENTIAL,
    syncType: null, // Sequential hat keinen syncType
    duration: 30000, // 30 Sekunden max
    component: 'LuckyWheelMinigame',
    rarity: RARITY.RARE,
    enabled: false, // Noch nicht implementiert
    rewards: {
      // Bei Glücksspielen werden Rewards dynamisch berechnet
      // Diese Werte sind Basis-Multiplikatoren
      points: [10, 6, 4, 2, 1, 0],
      coins: [6, 4, 2, 1, 0, 0],
    },
    config: {
      segments: [1, 2, 3, 5, 10, 0, 2, 3], // Rad-Segmente
    },
    messages: {
      announce: 'Minispiel: Glücksrad!',
      start: 'Drehe das Rad!',
      spin: '{player} dreht...',
      result: '{player} gewinnt {value}!',
      end: 'Alle haben gedreht!',
    },
  },

  // === GEO QUIZ ===
  GEO_QUIZ: {
    id: 'geoQuiz',
    name: 'Erdkunde-Quiz',
    description: 'Finde die richtige Stadt!',
    detailedDescription: 'Welche Stadt liegt in welchem Land? Schnelle richtige Antworten bringen mehr Punkte!',
    tutorial: 'Wähle die Stadt, die in dem gezeigten Land liegt. Schnellere richtige Antworten = mehr Punkte!',
    icon: '🌍',
    category: MINIGAME_CATEGORY.SKILL,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    duration: 20000, // 20 Sekunden
    component: 'GeoQuizMinigame',
    rarity: RARITY.COMMON,
    enabled: true,
    rewards: {
      // Punkte nach Geschwindigkeit: Erster = Spieleranzahl Punkte, dann absteigend
      // Kein Geld - nur Punkte (dynamisch im Handler berechnet)
      points: 'dynamic',
      coins: [],
    },
    config: {
      optionCount: 3, // Anzahl Antwortoptionen
    },
    messages: {
      announce: 'Minispiel: Erdkunde-Quiz!',
      start: 'Finde die richtige Stadt!',
      end: 'Zeit abgelaufen!',
    },
  },

  // === MATH QUIZ ===
  MATH_QUIZ: {
    id: 'mathQuiz',
    name: 'Mathe-Quiz',
    description: 'Löse die Rechenaufgabe!',
    detailedDescription: 'Wer rechnet am schnellsten? Löse die Multiplikation und wähle die richtige Antwort!',
    tutorial: 'Berechne das Ergebnis und wähle die richtige Antwort!\nSchnelle Antworten = mehr Punkte!',
    icon: '🔢',
    category: MINIGAME_CATEGORY.SKILL,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    duration: 20000,
    component: 'MathQuizMinigame',
    rarity: RARITY.COMMON,
    enabled: true,
    rewards: {
      points: 'dynamic',
      coins: [],
    },
    config: {
      optionCount: 3,
    },
    messages: {
      announce: 'Minispiel: Mathe-Quiz!',
      start: 'Löse die Aufgabe!',
      end: 'Zeit abgelaufen!',
    },
  },

  // === MUSIC QUIZ ===
  MUSIC_QUIZ: {
    id: 'musicQuiz',
    name: 'Musik-Quiz',
    description: 'Errate das Erscheinungsjahr!',
    detailedDescription: 'In welchem Jahr erschien dieses Album? Schnelle richtige Antworten bringen mehr Punkte!',
    tutorial: 'Wähle das richtige Erscheinungsjahr des Albums!\nSchnelle Antworten = mehr Punkte!',
    icon: '🎵',
    category: MINIGAME_CATEGORY.SKILL,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    duration: 20000,
    component: 'MusicQuizMinigame',
    rarity: RARITY.COMMON,
    enabled: true,
    rewards: {
      points: 'dynamic',
      coins: [],
    },
    config: {
      optionCount: 3,
    },
    messages: {
      announce: 'Minispiel: Musik-Quiz!',
      start: 'Errate das Erscheinungsjahr!',
      end: 'Zeit abgelaufen!',
    },
  },

  // === MISSING CARD (Memory Quiz) ===
  MISSING_CARD: {
    id: 'missingCard',
    name: 'Rate die Karte!',
    description: 'Welche Karte hast du gesehen?',
    detailedDescription: '5 Karten erscheinen kurz - merke sie dir! Dann siehst du 5 neue Karten, aber nur eine davon war dabei. Finde sie!',
    tutorial: 'Merke dir die 5 Karten!\nDanach siehst du 5 Optionen.\nNur EINE davon war dabei - finde sie!',
    icon: '🃏',
    category: MINIGAME_CATEGORY.MEMORY,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    duration: 20000,
    component: 'MissingCardMinigame',
    rarity: RARITY.COMMON,
    enabled: true,
    rewards: {
      points: 'dynamic',
      coins: [],
    },
    config: {
      cardCount: 5,
      optionCount: 5,
      memorizationTime: 4000,
    },
    messages: {
      announce: 'Minispiel: Rate die Karte!',
      start: 'Merke dir die Karten!',
      question: 'Welche Karte hast du gesehen?',
      end: 'Zeit abgelaufen!',
    },
  },

  // === CATCH STICK (Simultaneous Skill Game) ===
  CATCH_STICK: {
    id: 'catchStick',
    name: 'Fang den Stock!',
    description: 'Fange den fallenden Stock!',
    detailedDescription: 'Wähle die Größe deines Stocks - kleiner = schwieriger aber mehr Punkte. Dann fange ihn, wenn er fällt!',
    tutorial: 'Klicke zweimal. Einmal für die Stockgröße und wenn er die Linie kreuzt. Je mittiger und kleiner, je mehr Punkte.',
    icon: '🫲🏻',
    category: MINIGAME_CATEGORY.SKILL,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.ISOLATED,
    revealDuration: 1500, // Kurze Pause vor Ranking (kein Quiz → keine lange Reveal-Phase)
    duration: 30000, // 30 Sekunden (sizing 5s + lift 3s + wait 3s + fall ~1s = ~12s)
    component: 'CatchStickMinigame',
    rarity: RARITY.UNCOMMON,
    enabled: true,
    rewards: {
      // Punkte dynamisch basierend auf Score (wie GeoQuiz)
      // Keine Münzen - nur Punkte
      points: 'dynamic',
      coins: [],
    },
    config: {
      stickMin: 50,          // Minimale Stockgröße in px
      stickMax: 200,         // Maximale Stockgröße in px
      stickWidthMin: 12,     // Minimale Stockbreite in px
      stickWidthMax: 36,     // Maximale Stockbreite in px
      sizingDuration: 5000,  // Zeit für Größenwahl in ms
      liftDuration: 3000,    // Zeit für Hebe-Animation in ms
      fallDuration: 850,     // Falldauer in ms
      catchYPercent: 79,     // Position der Fang-Linie in % von oben
      delayMin: 1000,        // Minimale Wartezeit vor Fall
      delayMax: 3000,        // Maximale Wartezeit vor Fall
      tutorialDuration: 7000,  // Tutorial-Overlay Dauer in ms
      // Scoring labels based on precision percentage
      scoringLabels: [
        { threshold: 98, label: 'BOSS' },
        { threshold: 90, label: 'SNOB' },
        { threshold: 75, label: 'NORMIE' },
        { threshold: 40, label: 'PLEB' },
        { threshold: 0, label: 'ASSO' },
      ],
    },
    messages: {
      announce: 'Minispiel: Fang den Stock!',
      start: 'Wähle die Stockgröße!',
      sizing: 'GRÖSSE WÄHLEN',
      sizingHint: 'Kleiner = mehr Mult.',
      lifting: 'Stock fliegt in Position...',
      waiting: 'WARTE!',
      waitingHint: 'Gleich fällt er...',
      dropping: 'Jetzt fangen!',
      miss: 'DANEBEN!',
      missHint: 'Das war gar nix.',
      end: 'Alle haben gespielt!',
    },
  },

  // === COLLISION COURSE (Interactive Lane Game) ===
  COLLISION_COURSE: {
    id: 'collisionCourse',
    name: 'Kollisionskurs',
    description: 'Spur wechseln, Kollisionen vermeiden, Fallen legen!',
    detailedDescription: 'Alle steuern gleichzeitig ihre Spur. Kollisionen bremsen, Fallen blockieren. Wer am weitesten kommt, gewinnt.',
    tutorial: 'Wähle jede Runde eine Spur.\nKollision = langsamer.\nFalle trifft die nächste Runde.',
    icon: '💥',
    category: MINIGAME_CATEGORY.STRATEGY,
    mode: MINIGAME_MODE.SIMULTANEOUS,
    syncType: SYNC_TYPE.INTERACTIVE,
    duration: 18000, // 12 Ticks * 1500ms
    component: 'CollisionCourseMinigame',
    rarity: RARITY.UNCOMMON,
    enabled: true,
    rewards: {
      points: 'dynamic',
      coins: [],
    },
    config: {
      laneCount: 3,
      trackLength: 24,
      tickCount: 12,
      tickDuration: 1500,
      baseMove: 2,
      collisionPenalty: 1,
      trapPenalty: 1,
      trapDurationTicks: 2,
      trapUses: 1,
      defaultLane: 1, // 0=links,1=mitte,2=rechts
    },
    messages: {
      announce: 'Minispiel: Kollisionskurs!',
      start: 'Spur wählen!',
      end: 'Ziel erreicht!',
    },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Hole ein Minispiel nach ID
 */
function getMinigameById(id) {
  return Object.values(MINIGAMES).find(m => m.id === id);
}

/**
 * Hole alle aktivierten Minispiele
 */
function getEnabledMinigames() {
  return Object.values(MINIGAMES).filter(m => m.enabled);
}

/**
 * Hole Minispiele nach Kategorie
 */
function getMinigamesByCategory(category) {
  return Object.values(MINIGAMES).filter(m => m.category === category && m.enabled);
}

/**
 * Hole Minispiele nach Rarität
 */
function getMinigamesByRarity(rarity) {
  return Object.values(MINIGAMES).filter(m => m.rarity === rarity && m.enabled);
}

/**
 * Wähle ein zufälliges aktiviertes Minispiel
 * Berücksichtigt Rarität für gewichtete Auswahl
 * @param {string} [excludeId] - ID eines Minispiels das ausgeschlossen werden soll (Repeat-Schutz)
 */
function getRandomMinigame(excludeId) {
  let enabled = getEnabledMinigames();
  if (enabled.length === 0) return null;

  // Repeat-Schutz: letztes Minispiel ausschließen (nur wenn mehr als 1 verfügbar)
  if (excludeId && enabled.length > 1) {
    enabled = enabled.filter(m => m.id !== excludeId);
  }

  // Gewichtete Auswahl basierend auf Rarität
  const weights = {
    [RARITY.COMMON]: 50,
    [RARITY.UNCOMMON]: 30,
    [RARITY.RARE]: 15,
    [RARITY.LEGENDARY]: 5,
  };

  const weighted = [];
  for (const game of enabled) {
    const weight = weights[game.rarity] || 10;
    for (let i = 0; i < weight; i++) {
      weighted.push(game);
    }
  }

  const index = Math.floor(Math.random() * weighted.length);
  return weighted[index];
}

/**
 * Berechne Belohnungen für einen Spieler basierend auf Platzierung
 * @param {Object} minigame - Minispiel-Definition
 * @param {number} rank - Platzierung (1-basiert)
 * @param {number} totalPlayers - Anzahl Spieler
 * @param {number} score - Score aus dem Handler (für dynamische Punkte)
 */
function calculateRewards(minigame, rank, totalPlayers, score = 0) {
  const rewards = minigame.rewards;
  const index = rank - 1; // rank ist 1-basiert

  // 'dynamic' bedeutet: Punkte kommen direkt vom Handler-Score
  const points = rewards.points === 'dynamic'
    ? score
    : (rewards.points?.[index] || 0);

  return {
    points,
    coins: rewards.coins?.[index] || 0,
    mult: rewards.mult?.[index] || 0,
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

module.exports = {
  MINIGAME_CATEGORY,
  MINIGAMES,
  getMinigameById,
  getEnabledMinigames,
  getMinigamesByCategory,
  getMinigamesByRarity,
  getRandomMinigame,
  calculateRewards,
};
